// =============================================================================
// MeuDriver — TripInstructorPanel (lado instrutor)
// -----------------------------------------------------------------------------
// Controles do deslocamento: "Estou a caminho" → "Cheguei" → "Aluno embarcou".
//
// Também é o lugar onde o consentimento de localização é pedido e revogado. Os
// dois ficam juntos de propósito: o instrutor decide compartilhar no mesmo
// momento em que vê para quê serve, e consegue desligar sem procurar em
// configurações.
//
// Ver docs/mapa/modulos/rastreamento-ao-vivo.md
// =============================================================================

import { useState } from 'react'
import { toast } from 'sonner'
import { Navigation, MapPin, Check, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useTripTracking } from '@/hooks/useTripTracking'
import { useTripConsent, useSetTripConsent } from '@/hooks/useTripConsent'

interface Props {
  bookingId: string
  studentName?: string | null
  pickupAddress?: string | null
}

export function TripInstructorPanel({ bookingId, studentName, pickupAddress }: Props) {
  const { status, isTracking, lastPosition, error, backgroundCapable, start, advance } =
    useTripTracking(bookingId)
  const { data: hasConsent, isLoading: consentLoading } = useTripConsent()
  const setConsent = useSetTripConsent()
  const [busy, setBusy] = useState(false)

  async function run(fn: () => Promise<void>, successMsg: string) {
    setBusy(true)
    try {
      await fn()
      toast.success(successMsg)
    } catch {
      // useTripTracking já traduziu o erro para `error`; o toast evita que a
      // ação pareça ter funcionado silenciosamente.
      toast.error('Não foi possível concluir a ação.')
    } finally {
      setBusy(false)
    }
  }

  // ── Consentimento ──────────────────────────────────────────────────────────

  if (!consentLoading && !hasConsent) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand-600" />
            Compartilhar localização durante o deslocamento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Ao ativar, o aluno vê sua posição no mapa <strong>apenas</strong> entre o
            momento em que você toca em &quot;Estou a caminho&quot; e o embarque. Fora
            dessa janela, ninguém acompanha você.
          </p>
          <p className="text-xs text-muted-foreground">
            O trajeto fica guardado por 7 dias, só para resolver eventual disputa
            sobre a aula. Você pode desativar quando quiser.
          </p>
          <Button
            onClick={() =>
              setConsent.mutate(true, {
                onSuccess: () => toast.success('Localização ativada.'),
                onError: () => toast.error('Não foi possível ativar.'),
              })
            }
            disabled={setConsent.isPending}
          >
            {setConsent.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Ativar compartilhamento
          </Button>
        </CardContent>
      </Card>
    )
  }

  // ── Deslocamento concluído ─────────────────────────────────────────────────

  if (status === 'embarcado' || status === 'encerrado') {
    return (
      <Card>
        <CardContent className="py-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Check className="h-4 w-4 text-green-600" />
          Aluno embarcado. O rastreamento foi encerrado.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Navigation className="h-4 w-4 text-brand-600" />
            Deslocamento
          </CardTitle>
          {isTracking && (
            <Badge className="bg-green-100 text-green-800 border-green-200">
              <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-green-600 animate-pulse" />
              Ao vivo
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {pickupAddress && (
          <p className="text-sm text-muted-foreground flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            {pickupAddress}
          </p>
        )}

        {/* Não prometemos o que o PWA não entrega. */}
        {isTracking && !backgroundCapable && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            Mantenha esta tela aberta. No navegador, a localização para de ser enviada
            quando o app sai de foco ou a tela é bloqueada.
          </p>
        )}

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
            {error}
          </p>
        )}

        {isTracking && lastPosition?.accuracyM != null && lastPosition.accuracyM > 100 && (
          <p className="text-xs text-muted-foreground">
            Precisão do GPS baixa ({Math.round(lastPosition.accuracyM)} m). O aluno pode
            ver sua posição deslocada.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {(status === 'nao_iniciado' || status === 'cancelado' || status === null) && (
            <Button onClick={() => run(start, 'Deslocamento iniciado.')} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Estou a caminho
            </Button>
          )}

          {status === 'a_caminho' && (
            <>
              <Button
                onClick={() => run(() => advance('chegou'), 'Chegada registrada.')}
                disabled={busy}
              >
                Cheguei ao ponto
              </Button>
              <Button
                variant="outline"
                onClick={() => run(() => advance('cancelado'), 'Deslocamento cancelado.')}
                disabled={busy}
              >
                Cancelar
              </Button>
            </>
          )}

          {status === 'chegou' && (
            <Button
              onClick={() =>
                run(() => advance('embarcado'), `${studentName ?? 'Aluno'} embarcou. Boa aula!`)
              }
              disabled={busy}
            >
              Aluno embarcou
            </Button>
          )}
        </div>

        <button
          type="button"
          className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
          onClick={() =>
            setConsent.mutate(false, {
              onSuccess: () => toast.success('Compartilhamento desativado.'),
              onError: () => toast.error('Não foi possível desativar.'),
            })
          }
        >
          Desativar compartilhamento de localização
        </button>
      </CardContent>
    </Card>
  )
}

export default TripInstructorPanel
